/**
 * Social (Sociales) design-store catalog. Mirrors the other widget catalogs: a
 * static gallery of self-contained lower-third card SVGs the user can apply to
 * the Sociales (SOCIALS) widget. Recreated 1:1 from the Claude Design
 * `Social Widgets.html` handoff (10 variants), cropped from the 1920×1080 scene
 * to the social bar.
 *
 * PURE-DATA FORMAT: each design is a {@link SerializableDesign} (plain data, no
 * JS `build()`), so it can be stored / transmitted / PUBLISHED safely — no code
 * is ever transported or run. The generic {@link renderDesign} turns the data
 * into the final SVG; `renderSocialDesignSvg` is the single entry point the
 * Store thumbnail, the config preview and the live Pixi renderer all call.
 *
 * The card SVGs keep the original ABSOLUTE scene coordinates and just set
 * `viewBox` to the card crop (`x y w h`). The only dynamic piece is the
 * streamer's @handle (the platform glyphs / labels are decorative, matching the
 * mock), bound to the single `{{handle}}` slot.
 *
 * SECURITY: the handle is integration-derived (a platform username/displayName),
 * i.e. NOT a fixed enum — it can contain arbitrary characters. The `handle`
 * slot is `kind:"text"` with `escape:true` + `cap:28`, so `renderDesign`
 * truncates then XML-escapes it (the renderer is the single escaping
 * choke-point) before it touches the markup. Every defs / gradient / filter /
 * pattern / glyph id is prefixed per design so multiple cards inline into one
 * DOM document without id collisions.
 *
 * Known caveat: the original glyph symbols (twitch/youtube/instagram) were a
 * shared `<defs>` in the mock; here each card inlines its own prefixed copy.
 */

import { renderDesign } from "../entities/designs/renderDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";
import type { SerializableDesign } from "../entities/designs/serializableDesign";

export type SocialDesignCategory = "social";

/**
 * A social design is a {@link SerializableDesign} narrowed to the social widget
 * (its `category` is always `"social"`). It exposes only pure data; the dynamic
 * @handle lives in the single `{{handle}}` slot.
 */
export interface SocialDesign extends SerializableDesign {
  widgetType: "social";
  category: SocialDesignCategory;
}

/** Default handle for thumbnails / previews when no integration is connected. */
export const SAMPLE_HANDLE = "@eriol.streams";

/** Shared `handle` slot: user-controlled text, capped at 28 then XML-escaped. */
const handleSlot = {
  kind: "text",
  escape: true,
  cap: 28,
  fallback: SAMPLE_HANDLE,
} as const;

// ── Reusable platform glyph paths (from the mock's shared <defs>) ──────────
const G_TWITCH =
  "M4 0 L0 4 v22 h7 v4 h4 l4 -4 h6 l9 -9 V0 Z M30 16 l-5 5 h-6 l-4 4 v-4 H10 V3 h20 Z M24 7 v9 h-3 V7 Z M17 7 v9 h-3 V7 Z";
const G_YT =
  "M31 5 a4 4 0 0 0 -3 -3 C25 1 16 1 16 1 s-9 0 -12 1 a4 4 0 0 0 -3 3 C0 8 0 12 0 12 s0 4 1 7 a4 4 0 0 0 3 3 c3 1 12 1 12 1 s9 0 12 -1 a4 4 0 0 0 3 -3 c1 -3 1 -7 1 -7 s0 -4 -1 -7 Z M13 17 V7 l8 5 Z";
const G_IG =
  "M9 0 H23 a9 9 0 0 1 9 9 V23 a9 9 0 0 1 -9 9 H9 a9 9 0 0 1 -9 -9 V9 a9 9 0 0 1 9 -9 Z M9 4 a5 5 0 0 0 -5 5 V23 a5 5 0 0 0 5 5 H23 a5 5 0 0 0 5 -5 V9 a5 5 0 0 0 -5 -5 Z M16 8 a8 8 0 1 1 0 16 a8 8 0 0 1 0 -16 Z M16 12 a4 4 0 1 0 0 8 a4 4 0 0 0 0 -8 Z M25 6 a2 2 0 1 1 0 4 a2 2 0 0 1 0 -4 Z";

/** The three common glyph <path> defs, ids prefixed per design. */
const glyphDefs = (p: string): string =>
  `<path id="${p}-g-twitch" d="${G_TWITCH}"/><path id="${p}-g-yt" d="${G_YT}"/><path id="${p}-g-ig" d="${G_IG}"/>`;

// ── Designs ────────────────────────────────────────────────────────────────

const darkDesign: SocialDesign = {
  id: "dark",
  name: "Dark Minimal",
  subtitle: "Icon rail",
  widgetType: "social",
  category: "social",
  description:
    "Charcoal chip with three platform glyphs in rounded tiles, the @handle and a soft accent. Clean lower-third.",
  swatches: ["#0d0d12", "#15151d", "#a07dff", "#3ad96b"],
  viewBox: "530 776 860 250",
  cardWidth: 860,
  cardHeight: 250,
  fonts: ["DM Mono", "Space Grotesk"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 776 860 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs>
    <linearGradient id="sod-panel" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#16161e"/><stop offset="100%" stop-color="#0b0b10"/></linearGradient>
    <filter id="sod-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="14" stdDeviation="22" flood-color="#000" flood-opacity=".55"/></filter>
    ${glyphDefs("sod")}
  </defs>
  <g transform="translate(560 800)">
    <rect width="800" height="160" rx="34" fill="url(#sod-panel)" stroke="#26262f" stroke-width="1.5" filter="url(#sod-shadow)"/>
    <rect x="1.5" y="1.5" width="797" height="60" rx="32" fill="#fff" opacity=".03"/>
    <text x="48" y="64" font-family="DM Mono, monospace" font-size="18" fill="#8a8f9c" letter-spacing="6">SEGUINOS EN</text>
    <text x="48" y="118" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="46" fill="#f5f5f5">{{handle}}</text>
    <g transform="translate(560 50)">
      <g><rect width="60" height="60" rx="16" fill="#1f1830"/><use href="#sod-g-twitch" transform="translate(14 14)" fill="#a07dff"/></g>
      <g transform="translate(74 0)"><rect width="60" height="60" rx="16" fill="#241318"/><use href="#sod-g-yt" transform="translate(14 18)" fill="#ff4d6a"/></g>
      <g transform="translate(148 0)"><rect width="60" height="60" rx="16" fill="#241622"/><use href="#sod-g-ig" transform="translate(14 14)" fill="#ff8ed4"/></g>
    </g>
  </g>
</svg>`,
};

const brandDesign: SocialDesign = {
  id: "brand",
  name: "Brand Stack",
  subtitle: "Per-platform rows",
  widgetType: "social",
  category: "social",
  description:
    "Three stacked rows, each with its brand-colored glyph tile (Twitch / YouTube / Instagram) and the handle.",
  swatches: ["#101014", "#9146ff", "#ff0033", "#e1306c"],
  viewBox: "580 666 760 410",
  cardWidth: 760,
  cardHeight: 410,
  fonts: ["Space Grotesk", "DM Mono"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="580 666 760 410" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs><filter id="sob-shadow" x="-20%" y="-20%" width="140%" height="160%"><feDropShadow dx="0" dy="14" stdDeviation="22" flood-color="#000" flood-opacity=".55"/></filter>${glyphDefs("sob")}</defs>
  <g transform="translate(610 690)">
    <rect width="700" height="320" rx="20" fill="#101014" stroke="#26262e" stroke-width="1.5" filter="url(#sob-shadow)"/>
    <text x="36" y="58" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="22" fill="#fff" letter-spacing="2">SEGUINOS</text>
    <line x1="36" y1="76" x2="664" y2="76" stroke="#23232c"/>
    <g font-family="Space Grotesk, sans-serif" font-weight="600" font-size="30">
      <g transform="translate(36 100)"><rect width="64" height="64" rx="16" fill="#9146ff"/><use href="#sob-g-twitch" transform="translate(16 16)" fill="#fff"/><text x="88" y="24" font-size="14" font-family="DM Mono, monospace" fill="#9c7bff" letter-spacing="2">TWITCH</text><text x="88" y="55" fill="#fff">{{handle}}</text></g>
      <g transform="translate(36 178)"><rect width="64" height="64" rx="16" fill="#ff0033"/><use href="#sob-g-yt" transform="translate(16 20)" fill="#fff"/><text x="88" y="24" font-size="14" font-family="DM Mono, monospace" fill="#ff5a76" letter-spacing="2">YOUTUBE</text><text x="88" y="55" fill="#fff">{{handle}}</text></g>
      <g transform="translate(36 256)"><rect width="64" height="64" rx="16" fill="#e1306c"/><use href="#sob-g-ig" transform="translate(16 16)" fill="#fff"/><text x="88" y="24" font-size="14" font-family="DM Mono, monospace" fill="#ff7aa8" letter-spacing="2">INSTAGRAM</text><text x="88" y="55" fill="#fff">{{handle}}</text></g>
    </g>
  </g>
</svg>`,
};

const y2kDesign: SocialDesign = {
  id: "y2k",
  name: "Y2K Aqua Gel",
  subtitle: "Gel buttons",
  widgetType: "social",
  category: "social",
  description:
    "Glossy aqua capsule with gel glyph buttons, chrome highlight and a bouncy tag.",
  swatches: ["#003a7a", "#1c8fff", "#8acfff", "#5aff8a"],
  viewBox: "530 776 860 250",
  cardWidth: 860,
  cardHeight: 250,
  fonts: ["Audiowide"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 776 860 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs>
    <linearGradient id="soy-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeaff"/><stop offset="55%" stop-color="#1c8fff"/><stop offset="100%" stop-color="#003a7a"/></linearGradient>
    <linearGradient id="soy-btn" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#eaf6ff"/><stop offset="100%" stop-color="#7fc4ff"/></linearGradient>
    <filter id="soy-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="14" stdDeviation="20" flood-color="#001026" flood-opacity=".5"/></filter>
    ${glyphDefs("soy")}
  </defs>
  <g transform="translate(560 800)">
    <rect width="800" height="160" rx="44" fill="url(#soy-bg)" stroke="#fff" stroke-width="3" filter="url(#soy-shadow)"/>
    <ellipse cx="400" cy="32" rx="360" ry="12" fill="#fff" opacity=".7"/>
    <text x="48" y="66" font-family="Audiowide, sans-serif" font-size="22" fill="#fff" letter-spacing="2">SEGUINOS ♥</text>
    <text x="48" y="120" font-family="Audiowide, sans-serif" font-size="40" fill="#fff">{{handle}}</text>
    <g transform="translate(556 44)">
      <g><rect width="68" height="68" rx="22" fill="url(#soy-btn)" stroke="#fff" stroke-width="2"/><ellipse cx="34" cy="14" rx="26" ry="6" fill="#fff" opacity=".7"/><use href="#soy-g-twitch" transform="translate(18 18)" fill="#1c5fa0"/></g>
      <g transform="translate(80 0)"><rect width="68" height="68" rx="22" fill="url(#soy-btn)" stroke="#fff" stroke-width="2"/><ellipse cx="34" cy="14" rx="26" ry="6" fill="#fff" opacity=".7"/><use href="#soy-g-yt" transform="translate(18 22)" fill="#1c5fa0"/></g>
      <g transform="translate(160 0)"><rect width="68" height="68" rx="22" fill="url(#soy-btn)" stroke="#fff" stroke-width="2"/><ellipse cx="34" cy="14" rx="26" ry="6" fill="#fff" opacity=".7"/><use href="#soy-g-ig" transform="translate(18 18)" fill="#1c5fa0"/></g>
    </g>
  </g>
</svg>`,
};

const cyberDesign: SocialDesign = {
  id: "cyber",
  name: "Cyberpunk Neon",
  subtitle: "HUD socials",
  widgetType: "social",
  category: "social",
  description:
    "Chamfered HUD with neon glyph cells, CONNECT:// tag, katakana accent and a glowing handle.",
  swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
  viewBox: "530 776 860 250",
  cardWidth: 860,
  cardHeight: 250,
  fonts: ["DM Mono", "Orbitron"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 776 860 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs>
    <filter id="soc-g"><feGaussianBlur stdDeviation="2"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="soc-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#00f0ff" opacity=".07"/></pattern>
    ${glyphDefs("soc")}
  </defs>
  <g transform="translate(560 800)" filter="url(#soc-g)">
    <polygon points="0,30 30,0 770,0 800,30 800,130 770,160 30,160 0,130" fill="#080010" stroke="#ff2e88" stroke-width="2.5"/>
    <polygon points="12,36 38,12 762,12 788,36 788,124 762,148 38,148 12,124" fill="none" stroke="#00f0ff" stroke-width="1.5" opacity=".7"/>
    <rect x="12" y="12" width="776" height="136" fill="url(#soc-scan)"/>
    <text x="34" y="56" font-family="DM Mono, monospace" font-size="16" fill="#f5d019" letter-spacing="5">▮ CONNECT:// ソーシャル</text>
    <text x="34" y="120" font-family="Orbitron, sans-serif" font-weight="700" font-size="48" fill="#00f0ff">{{handle}}</text>
    <g transform="translate(596 46)">
      <g><polygon points="0,10 10,0 56,0 66,10 66,56 56,66 10,66 0,56" fill="#0a0014" stroke="#ff2e88" stroke-width="1.5"/><use href="#soc-g-twitch" transform="translate(16 16)" fill="#ff2e88"/></g>
      <g transform="translate(76 0)"><polygon points="0,10 10,0 56,0 66,10 66,56 56,66 10,66 0,56" fill="#0a0014" stroke="#00f0ff" stroke-width="1.5"/><use href="#soc-g-yt" transform="translate(16 20)" fill="#00f0ff"/></g>
      <g transform="translate(152 0)"><polygon points="0,10 10,0 56,0 66,10 66,56 56,66 10,66 0,56" fill="#0a0014" stroke="#f5d019" stroke-width="1.5"/><use href="#soc-g-ig" transform="translate(16 16)" fill="#f5d019"/></g>
    </g>
  </g>
</svg>`,
};

const editorialDesign: SocialDesign = {
  id: "editorial",
  name: "Editorial",
  subtitle: "Masthead",
  widgetType: "social",
  category: "social",
  description:
    "Cream banner with double rule, italic 'Seguinos en' and serif handle with hairline glyph rules.",
  swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
  viewBox: "530 776 860 250",
  cardWidth: 860,
  cardHeight: 250,
  fonts: ["Cormorant Garamond"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 776 860 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs><filter id="soe-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="12" stdDeviation="18" flood-color="#000" flood-opacity=".4"/></filter>${glyphDefs("soe")}</defs>
  <g transform="translate(560 800)">
    <rect width="800" height="160" fill="#f5efe2" filter="url(#soe-shadow)"/>
    <line x1="24" y1="22" x2="776" y2="22" stroke="#1a1815" stroke-width="3"/>
    <line x1="24" y1="30" x2="776" y2="30" stroke="#1a1815" stroke-width="1"/>
    <text x="40" y="86" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="34" fill="#8a3a2a">Seguinos en</text>
    <text x="40" y="134" font-family="Cormorant Garamond, serif" font-weight="600" font-size="52" fill="#1a1815">{{handle}}</text>
    <g transform="translate(580 56)" fill="#1a1815">
      <g><use href="#soe-g-twitch" transform="scale(1.4)"/></g>
      <line x1="62" y1="0" x2="62" y2="50" stroke="#c8a050"/>
      <g transform="translate(84 4)"><use href="#soe-g-yt" transform="scale(1.3)"/></g>
      <line x1="150" y1="0" x2="150" y2="50" stroke="#c8a050"/>
      <g transform="translate(170 0)"><use href="#soe-g-ig" transform="scale(1.4)"/></g>
    </g>
  </g>
</svg>`,
};

const pixelDesign: SocialDesign = {
  id: "pixel",
  name: "Pixel Arcade",
  subtitle: "Icon tiles",
  widgetType: "social",
  category: "social",
  description:
    "8-bit box with pixel-art platform tiles, a FOLLOW! ribbon and Press Start 2P handle.",
  swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
  viewBox: "530 760 860 270",
  cardWidth: 860,
  cardHeight: 270,
  fonts: ["Press Start 2P"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 760 860 270" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" shape-rendering="crispEdges">
  <defs>${glyphDefs("sop")}</defs>
  <g transform="translate(560 790)">
    <rect width="800" height="180" fill="#48cae4"/>
    <rect x="8" y="8" width="784" height="164" fill="#1d2a3a"/>
    <rect x="16" y="16" width="768" height="148" fill="#0a121c"/>
    <g fill="#ffb703"><rect width="24" height="24"/><rect x="776" width="24" height="24"/><rect y="156" width="24" height="24"/><rect x="776" y="156" width="24" height="24"/></g>
    <g transform="translate(40 -16)"><rect width="240" height="34" fill="#ff3d4d"/><rect x="6" y="6" width="228" height="22" fill="#1d2a3a"/><text x="120" y="23" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="13" fill="#ffb703">FOLLOW!</text></g>
    <text x="44" y="128" font-family="Press Start 2P, monospace" font-size="22" fill="#fff">{{handle}}</text>
    <g transform="translate(540 50)">
      <g><rect width="76" height="76" fill="#9146ff"/><rect x="8" y="8" width="60" height="60" fill="#1d2a3a"/><use href="#sop-g-twitch" transform="translate(22 22) scale(1.0)" fill="#9146ff"/></g>
      <g transform="translate(86 0)"><rect width="76" height="76" fill="#ff3d4d"/><rect x="8" y="8" width="60" height="60" fill="#1d2a3a"/><use href="#sop-g-yt" transform="translate(22 26) scale(1.0)" fill="#ff3d4d"/></g>
      <g transform="translate(172 0)"><rect width="76" height="76" fill="#48cae4"/><rect x="8" y="8" width="60" height="60" fill="#1d2a3a"/><use href="#sop-g-ig" transform="translate(22 22) scale(1.0)" fill="#48cae4"/></g>
    </g>
  </g>
</svg>`,
};

const cottageDesign: SocialDesign = {
  id: "cottage",
  name: "Cottagecore",
  subtitle: "Stitched tags",
  widgetType: "social",
  category: "social",
  description:
    "Linen ribbon with cross-stitch border, flower-bulleted platform names and italic Cormorant handle.",
  swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
  viewBox: "530 766 860 270",
  cardWidth: 860,
  cardHeight: 270,
  fonts: ["Cormorant Garamond"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 766 860 270" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs>
    <pattern id="soco-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="soco-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
    <g id="soco-fl"><circle r="8" fill="#ff9da8"/><circle cx="-8" cy="-4" r="6" fill="#ff9da8"/><circle cx="8" cy="-4" r="6" fill="#ff9da8"/><circle cx="-5" cy="6" r="6" fill="#ff9da8"/><circle cx="5" cy="6" r="6" fill="#ff9da8"/><circle r="3.5" fill="#ffd060"/></g>
    <filter id="soco-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="12" stdDeviation="18" flood-color="#3a2a14" flood-opacity=".4"/></filter>
  </defs>
  <g transform="translate(560 790)">
    <rect width="800" height="180" fill="url(#soco-linen)" stroke="#6b4a2a" stroke-width="2" filter="url(#soco-shadow)"/>
    <rect width="800" height="24" fill="url(#soco-st)"/>
    <rect y="156" width="800" height="24" fill="url(#soco-st)"/>
    <rect width="24" height="180" fill="url(#soco-st)"/>
    <rect x="776" width="24" height="180" fill="url(#soco-st)"/>
    <text x="48" y="80" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="34" fill="#5a7a4a">encontranos en…</text>
    <text x="48" y="128" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="48" fill="#2a1a08">{{handle}}</text>
    <g transform="translate(556 70)">
      <use href="#soco-fl"/><text x="20" y="8" font-family="Cormorant Garamond, serif" font-style="italic" font-size="24" fill="#6b4a2a">twitch</text>
      <use href="#soco-fl" transform="translate(0 40)"/><text x="20" y="48" font-family="Cormorant Garamond, serif" font-style="italic" font-size="24" fill="#6b4a2a">youtube</text>
      <use href="#soco-fl" transform="translate(140 0)"/><text x="160" y="8" font-family="Cormorant Garamond, serif" font-style="italic" font-size="24" fill="#6b4a2a">insta</text>
      <use href="#soco-fl" transform="translate(140 40)"/><text x="160" y="48" font-family="Cormorant Garamond, serif" font-style="italic" font-size="24" fill="#6b4a2a">tiktok</text>
    </g>
  </g>
</svg>`,
};

const brutalistDesign: SocialDesign = {
  id: "brutalist",
  name: "Brutalist",
  subtitle: "Marquee bar",
  widgetType: "social",
  category: "social",
  description:
    "Black slab with orange edge, huge Anton handle and hard glyph blocks.",
  swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
  viewBox: "530 776 874 250",
  cardWidth: 874,
  cardHeight: 250,
  fonts: ["DM Mono", "Anton"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 776 874 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs>${glyphDefs("sobr")}</defs>
  <g transform="translate(560 800)">
    <rect x="14" y="14" width="800" height="160" fill="#ff5a2a"/>
    <rect width="800" height="160" fill="#1a1a1a"/>
    <rect width="24" height="160" fill="#ff5a2a"/>
    <text x="48" y="56" font-family="DM Mono, monospace" font-size="15" fill="#ff5a2a" letter-spacing="10">// SEGUINOS EN TODAS</text>
    <text x="44" y="128" font-family="Anton, sans-serif" font-size="76" fill="#f5f5f5" letter-spacing="-1">{{handle}}</text>
    <g transform="translate(640 38)" fill="#1a1a1a">
      <g><rect width="44" height="44" fill="#ff5a2a"/><use href="#sobr-g-twitch" transform="translate(6 6)" fill="#1a1a1a"/></g>
      <g transform="translate(54 0)"><rect width="44" height="44" fill="#ff5a2a"/><use href="#sobr-g-yt" transform="translate(6 10)" fill="#1a1a1a"/></g>
      <g transform="translate(108 0)"><rect width="44" height="44" fill="#ff5a2a"/><use href="#sobr-g-ig" transform="translate(6 6)" fill="#1a1a1a"/></g>
    </g>
  </g>
</svg>`,
};

const animeDesign: SocialDesign = {
  id: "anime",
  name: "Anime VHS",
  subtitle: "フォロー",
  widgetType: "social",
  category: "social",
  description:
    "Pink double-stroke panel with cyan tracking ghost, katakana tag and RGB-split glyphs + handle.",
  swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
  viewBox: "530 776 866 250",
  cardWidth: 866,
  cardHeight: 250,
  fonts: ["DM Mono", "VT323"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 776 866 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs>
    <filter id="soa-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="soa-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".12"/></pattern>
    ${glyphDefs("soa")}
  </defs>
  <g transform="translate(560 800)" filter="url(#soa-rgb)">
    <rect x="6" y="0" width="800" height="160" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".55"/>
    <rect width="800" height="160" fill="#0a0218" stroke="#ff7ad0" stroke-width="4"/>
    <rect x="10" y="10" width="780" height="140" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".55"/>
    <rect width="800" height="160" fill="url(#soa-scan)"/>
    <g><rect width="240" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="4">フォロー · FOLLOW</text></g>
    <text x="32" y="130" font-family="VT323, monospace" font-size="64" fill="#fff7c2">{{handle}}</text>
    <g transform="translate(584 56)">
      <g><rect width="60" height="60" fill="#1a0a2a" stroke="#ff7ad0" stroke-width="2"/><use href="#soa-g-twitch" transform="translate(14 14)" fill="#ff7ad0"/></g>
      <g transform="translate(70 0)"><rect width="60" height="60" fill="#1a0a2a" stroke="#7ad0ff" stroke-width="2"/><use href="#soa-g-yt" transform="translate(14 18)" fill="#7ad0ff"/></g>
      <g transform="translate(140 0)"><rect width="60" height="60" fill="#1a0a2a" stroke="#fff7c2" stroke-width="2"/><use href="#soa-g-ig" transform="translate(14 14)" fill="#fff7c2"/></g>
    </g>
  </g>
</svg>`,
};

const liquidDesign: SocialDesign = {
  id: "liquid",
  name: "Liquid Glass",
  subtitle: "Frosted pills",
  widgetType: "social",
  category: "social",
  description:
    "Frosted glass bar over a cobalt gradient with translucent glyph pills and a glowing @handle.",
  swatches: ["#0a1230", "#1c3a7a", "#9ad4ff", "#ffffff"],
  viewBox: "530 776 860 250",
  cardWidth: 860,
  cardHeight: 250,
  fonts: ["Space Grotesk"],
  slots: { handle: handleSlot },
  frame: `<svg viewBox="530 776 860 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
  <defs>
    <radialGradient id="sol-bg" cx="40%" cy="20%" r="100%"><stop offset="0%" stop-color="#2a4a9a"/><stop offset="60%" stop-color="#0a1230"/><stop offset="100%" stop-color="#020615"/></radialGradient>
    <radialGradient id="sol-bok" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#9ad4ff" stop-opacity=".55"/><stop offset="100%" stop-color="#9ad4ff" stop-opacity="0"/></radialGradient>
    <filter id="sol-bl"><feGaussianBlur stdDeviation="22"/></filter>
    <filter id="sol-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="14" stdDeviation="22" flood-color="#000" flood-opacity=".55"/></filter>
    ${glyphDefs("sol")}
  </defs>
  <g transform="translate(560 800)">
    <rect width="800" height="160" rx="34" fill="url(#sol-bg)" stroke="rgba(255,255,255,.22)" stroke-width="1.5" filter="url(#sol-shadow)"/>
    <g filter="url(#sol-bl)" opacity=".6"><circle cx="160" cy="60" r="120" fill="url(#sol-bok)"/><circle cx="640" cy="140" r="130" fill="url(#sol-bok)"/></g>
    <rect x="1.5" y="1.5" width="797" height="56" rx="32" fill="#fff" opacity=".05"/>
    <text x="48" y="60" font-family="Space Grotesk, sans-serif" font-weight="600" font-size="18" fill="#bfe0ff" letter-spacing="4">SEGUINOS EN</text>
    <text x="48" y="120" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="46" fill="#fff">{{handle}}</text>
    <g transform="translate(560 50)">
      <g><rect width="60" height="60" rx="20" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.3)" stroke-width="1"/><use href="#sol-g-twitch" transform="translate(14 14)" fill="#cfeeff"/></g>
      <g transform="translate(74 0)"><rect width="60" height="60" rx="20" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.3)" stroke-width="1"/><use href="#sol-g-yt" transform="translate(14 18)" fill="#cfeeff"/></g>
      <g transform="translate(148 0)"><rect width="60" height="60" rx="20" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.3)" stroke-width="1"/><use href="#sol-g-ig" transform="translate(14 14)" fill="#cfeeff"/></g>
    </g>
  </g>
</svg>`,
};

export const SOCIAL_DESIGNS: SocialDesign[] = [
  darkDesign,
  brandDesign,
  y2kDesign,
  cyberDesign,
  editorialDesign,
  pixelDesign,
  cottageDesign,
  brutalistDesign,
  animeDesign,
  liquidDesign,
];

/**
 * Render a design's card SVG with the given @handle substituted in. Delegates
 * to the generic {@link renderDesign}; the handle is bound to the `{{handle}}`
 * slot, which truncates (cap 28) + XML-escapes it. An empty/falsy handle falls
 * back to {@link SAMPLE_HANDLE} (matching the previous `handle || SAMPLE_HANDLE`).
 */
export function renderSocialDesignSvg(
  design: SocialDesign,
  handle: string,
): string {
  return renderDesign(design, { values: { handle: handle || SAMPLE_HANDLE } });
}

export function getSocialDesign(
  id: string | undefined,
): SocialDesign | undefined {
  if (!id) {
    return undefined;
  }
  // First-party catalog wins (always offline, no install round-trip).
  const local = SOCIAL_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Fall back to an INSTALLED community design (hydrated into the in-memory
  // registry by useDesignLibrary). Constrained to "social" so a non-social id
  // can't satisfy a social lookup. The registry stores SerializableDesign; a
  // social-widgetType row is structurally a SocialDesign, so the cast is safe.
  return getInstalledDesign(id, "social") as SocialDesign | undefined;
}
